/**
 * One Looker tile drawn with the app's own components.
 * Text comes from Looker's `rendered` (its LookML value_format); charts read
 * the raw value. null is "no data" — never 0. Each chart type has its own
 * file under ./charts; the rules they draw by live in ./lookerChartData.
 *
 * @module components/looker/LookerTile
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LOOKER_TILE_SERIES_CAP, rowKey, type LookerField, type LookerRow, type LookerTile } from '@/services/looker/lookerTypes';
import { deltaOf, deltaTone, formatDelta, TONE_CLASS } from '@/services/looker/lookerDelta';
import { cellFormatStyle, cellText, recordEntries, toChartNumber } from './lookerChartData';
import { CartesianTileChart } from './charts/CartesianTileChart';
import { PieTileChart } from './charts/PieTileChart';
import { FunnelTileChart } from './charts/FunnelTileChart';
import { WaterfallTileChart } from './charts/WaterfallTileChart';
import { SingleValueTile } from './charts/SingleValueTile';
import { LookerTileImage } from './LookerTileImage';
import type { LookerImageRequest } from '@/services/looker/lookerApi';
import type { LookerTileWidth } from '@/services/looker/lookerTypes';
import type { AppTileState } from '@/hooks/useLookerAppTiles';
import { AppTileBody } from './app/AppTileBody';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import type { TileNotes } from '@/services/looker/lookerTileNotes';
import { cprFieldOf, targetCellOf } from '@/services/looker/lookerTargetCells';
import { TargetLine, TargetStatus, TargetValue, targetHeader } from './TargetCells';
import { ResultBadge, ResultMetricLines } from './ResultMetricNotes';

// Kept here for callers that already import it from the tile.
export { toChartNumber } from './lookerChartData';

const Message: React.FC<{ text: string }> = ({ text }) => (
  <p className="py-6 text-center text-sm text-neutral-700">{text}</p>
);

/** target / targetStatus: DEC5 columns after Cost per Result; they carry its field so keys stay unique. */
type Col = { kind: 'field' | 'delta' | 'target' | 'targetStatus'; field: LookerField };

/** Fields (+ Δ), then the two target columns right after Cost per Result and its Δ. */
const columnsOf = (tile: LookerTile): Col[] => {
  const compare = new Set(tile.options?.compareMeasures ?? []);
  const cpr = tile.targets ? cprFieldOf(tile) : null;
  return tile.fields.flatMap((f): Col[] => [
    { kind: 'field', field: f },
    ...(tile.previousByKey && compare.has(f.name) ? [{ kind: 'delta' as const, field: f }] : []),
    ...(cpr && f.name === cpr.name ? [{ kind: 'target' as const, field: f }, { kind: 'targetStatus' as const, field: f }] : []),
  ]);
};

/** rowKey → coverage chips (lookerTileNotes). */
type RowNotes = Readonly<Record<string, string[]>>;
const NO_ROW_NOTES: RowNotes = {};

/** Amber chips beside a row's first dimension cell: "Unclassified", "google ไม่ส่ง Clicks". */
const RowChips: React.FC<{ notes: readonly string[] | undefined }> = ({ notes }) => (!notes || notes.length === 0 ? null : (
  // The cell is nowrap; this box lets the line break between chips, never inside one.
  <span className="whitespace-normal">
    {(notes ?? []).map((n) => (
      <span key={n} className="ml-2 inline-block whitespace-nowrap rounded-full border border-warning-700 px-1.5 text-[11px] text-warning-700 print:whitespace-normal print:break-words">{n}</span>
    ))}
  </span>
));

const TileTable: React.FC<{ tile: LookerTile; rowNotes?: RowNotes }> = ({ tile, rowNotes = NO_ROW_NOTES }) => {
  const { t } = useTranslation();
  const totals = tile.totals && Object.keys(tile.totals).length > 0 ? tile.totals : null;
  const cols = columnsOf(tile);
  const fade = tile.options?.fadeBelow ?? null;
  const isFaded = (r: LookerRow) => {
    const v = fade ? r[fade.field]?.value : null;
    return fade !== null && typeof v === 'number' && v < fade.value;
  };
  const header = (c: Col) => {
    if (c.kind === 'delta') return t('looker.deltaColumn', { label: c.field.label });
    if (c.kind === 'target') return tile.targets ? targetHeader(t, tile.targets) : '';
    if (c.kind === 'targetStatus') return t('looker.targets.statusColumn');
    return c.field.role === 'share' ? t('looker.share') : c.field.label;
  };
  const isNumeric = (c: Col) => c.kind === 'delta' || c.kind === 'target' || (c.kind === 'field' && c.field.kind === 'measure');
  const body = (c: Col, r: LookerRow) => {
    if (c.kind === 'delta') return deltaCell(c.field, r);
    if ((c.kind === 'target' || c.kind === 'targetStatus') && tile.targets) {
      const cell = targetCellOf(tile, r);
      return c.kind === 'target' ? <TargetValue cell={cell} targets={tile.targets} /> : <TargetStatus cell={cell} targets={tile.targets} />;
    }
    return cellText(r[c.field.name]) ?? '—';
  };
  const firstDim = tile.fields.find((f) => f.kind === 'dimension')?.name ?? null;
  const deltaCell = (f: LookerField, r: LookerRow) => {
    const prev = tile.previousByKey?.[rowKey(tile.fields, r)];
    const d = deltaOf(toChartNumber(r[f.name]?.value ?? null), toChartNumber(prev?.[f.name]?.value ?? null));
    return <span className={`font-semibold ${TONE_CLASS[deltaTone(f.name, d)]}`}>{d === null ? '—' : formatDelta(d)}</span>;
  };
  return (
    <div className="mt-2 max-h-80 max-w-full overflow-x-auto overflow-y-auto rounded-xl border border-neutral-200 print:max-h-none print:overflow-visible">
      <table className="min-w-full text-sm print:w-full">
        <thead className="sticky top-0 bg-neutral-50">
          <tr>
            {cols.map((c) => (
              <th
                key={`${c.kind}:${c.field.name}`}
                className={`whitespace-nowrap print:whitespace-normal print:break-words px-3 py-2 text-xs font-semibold text-neutral-800 ${isNumeric(c) ? 'text-right' : 'text-left'}`}
              >
                {header(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tile.rows.map((r, i) => {
            const faded = isFaded(r);
            return (
              <tr
                key={i}
                className={`border-t border-neutral-100 hover:bg-neutral-50 ${faded ? 'italic text-neutral-700' : ''}`}
                title={faded && fade ? t('looker.lowVolume', { value: fade.value.toLocaleString() }) : undefined}
              >
                {cols.map((c) => (
                  <td
                    key={`${c.kind}:${c.field.name}`}
                    className={`whitespace-nowrap print:whitespace-normal print:break-words px-3 py-2 ${faded ? '' : 'text-neutral-900'} ${isNumeric(c) ? 'text-right tabular-nums' : ''}`}
                    style={c.kind === 'field' ? cellFormatStyle(tile.vis.conditionalFormats, c.field, r[c.field.name]) ?? undefined : undefined}
                  >
                    {body(c, r)}
                    {c.kind === 'field' && c.field.name === firstDim && <RowChips notes={rowNotes[rowKey(tile.fields, r)]} />}
                    {c.kind === 'field' && c.field.name === firstDim && <ResultBadge tile={tile} row={r} />}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {totals && (
          // Looker's own totals: over every row the filters match, not only the rows shown.
          <tfoot className="sticky bottom-0 border-t-2 border-neutral-200 bg-neutral-50">
            <tr>
              {cols.map((c, i) => (
                <td
                  key={`${c.kind}:${c.field.name}`}
                  className={`whitespace-nowrap print:whitespace-normal print:break-words px-3 py-2 font-semibold text-neutral-900 ${isNumeric(c) ? 'text-right tabular-nums' : ''}`}
                  style={(c.kind === 'field' && tile.vis.formatTotals && cellFormatStyle(tile.vis.conditionalFormats, c.field, totals[c.field.name])) || undefined}
                >
                  {c.kind !== 'field'
                    ? ''
                    : c.field.kind === 'measure' ? cellText(totals[c.field.name]) ?? '' : i === 0 ? t('looker.tableTotal') : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

const RecordView: React.FC<{ tile: LookerTile }> = ({ tile }) => (
  <dl className="mt-2 divide-y divide-neutral-100 text-sm">
    {recordEntries(tile).map((e) => (
      <div key={e.label} className="flex items-baseline justify-between gap-4 py-2.5">
        <dt className="text-neutral-700">{e.label}</dt>
        <dd className="text-base font-semibold tabular-nums text-neutral-900">{e.text}</dd>
      </div>
    ))}
  </dl>
);

/** Charts need a category and a value; without both the data still shows, as a table. */
const TileChart: React.FC<{ tile: LookerTile; rowNotes: RowNotes }> = ({ tile, rowNotes }) => {
  const hasDim = tile.fields.some((f) => f.kind === 'dimension');
  const hasMeasure = tile.fields.some((f) => f.kind === 'measure');
  if (!hasMeasure) return <TileTable tile={tile} rowNotes={rowNotes} />;
  if (tile.type === 'funnel') return <FunnelTileChart tile={tile} />;
  if (!hasDim) return <TileTable tile={tile} rowNotes={rowNotes} />;
  if (tile.type === 'pie') return <PieTileChart tile={tile} />;
  if (tile.type === 'waterfall') return <WaterfallTileChart tile={tile} />;
  return <CartesianTileChart tile={tile} />;
};

const TileBody: React.FC<{ tile: LookerTile; imageRequest?: LookerImageRequest; width?: LookerTileWidth; rowNotes: RowNotes }> = ({ tile, imageRequest, width, rowNotes }) => {
  const { t } = useTranslation();
  if (tile.error === 'unsupported') return <Message text={t('looker.tileUnsupported')} />;
  if (tile.error === 'failed') return <Message text={t('looker.tileFailed')} />;
  if (tile.rows.length === 0) return <Message text={t('looker.tileEmpty')} />;
  // A chart the app does not draw: Looker's own picture, the data underneath.
  if (tile.render && imageRequest) return <LookerTileImage tile={tile} request={imageRequest} fallback={<TileTable tile={tile} rowNotes={rowNotes} />} />;
  if (tile.type === 'single_value') return <><SingleValueTile tile={tile} width={width} /><TargetLine tile={tile} /></>;
  if (tile.type === 'record') return <RecordView tile={tile} />;
  if (tile.type === 'table') return <TileTable tile={tile} rowNotes={rowNotes} />;
  return <TileChart tile={tile} rowNotes={rowNotes} />;
};

interface CardProps {
  tile: LookerTile;
  imageRequest?: LookerImageRequest;
  width?: LookerTileWidth;
  /** An app tile's state from useLookerAppTiles; undefined draws it as loading. */
  appState?: AppTileState;
  /** A channel filter is on (app boxes ignore it and say so). */
  channelFiltered?: boolean;
  /** Coverage notes (lookerTileNotes): card footer lines and row chips. Absent = none. */
  notes?: TileNotes;
  /** The page's coverage state, for box 8. Absent = box 8 has no coverage section (the builder preview). */
  coverage?: CoverageState;
}

export const LookerTileCard: React.FC<CardProps> = ({ tile, imageRequest, width, appState, channelFiltered, notes, coverage }) => {
  const { t } = useTranslation();
  return (
    // flex-1: as tall as its row-mates in the grid. Its content stays in block flow, top-aligned
    // (margins collapse as before), so the spare height sits at the bottom.
    <section className="min-w-0 flex-1 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-neutral-900">{tile.title}</h3>
      {/* An app tile has no Looker rows: the footers below all stay quiet for it. */}
      {tile.type === 'app' ? <AppTileBody state={appState} channelFiltered={channelFiltered} coverage={coverage} /> : <TileBody tile={tile} imageRequest={imageRequest} width={width} rowNotes={notes?.rows ?? NO_ROW_NOTES} />}
      {/* Not reported is not zero: say which platform did not send the Result metric. */}
      {(notes?.card ?? []).map((n) => <p key={n} className="mt-2 text-xs font-medium text-warning-700">{n}</p>)}
      {/* DEC3: which Result definition this tile counts (badge, reach caveat, failed read, central). */}
      <ResultMetricLines tile={tile} />
      {/* An empty cut has no rows and its through-date may lie in the previous period: no note then. */}
      {tile.resultThrough && !tile.error && tile.rows.length > 0 && (
        <p className="mt-2 text-xs text-neutral-700">
          {tile.resultPrevious
            ? t('looker.resultThroughCompared', { date: tile.resultThrough, prevStart: tile.resultPrevious.start, prevEnd: tile.resultPrevious.end })
            : t('looker.resultThrough', { date: tile.resultThrough })}
          {tile.type === 'table' && <> · {tile.resultMetric?.awareness === 'reach' ? t('looker.resultBasisReach') : t('looker.resultBasis')}</>}
        </p>
      )}
      {tile.truncated && !tile.error && tile.rows.length > 0 && (
        <p className="mt-2 text-xs text-neutral-700">{t('looker.tileTruncated', { count: tile.rows.length })}</p>
      )}
      {tile.seriesTruncated && !tile.error && tile.rows.length > 0 && (
        <p className="mt-2 text-xs text-neutral-700">{t('looker.tileSeriesTruncated', { count: LOOKER_TILE_SERIES_CAP })}</p>
      )}
    </section>
  );
};
